import '@src/modules/shared/ui/infrastructure/styles/main.css';
import { getSqlJsWasmBinary } from 'sqljs-wasm-browser-binary';
import '@src/modules/auth/infrastructure/drivers/LoginComponent.ts';
import '@src/modules/shared/ui/infrastructure/drivers/ui/components/LayoutComponent.ts';
import { AppConfig } from '@core/domain/AppConfig.ts';
import { AppContainer } from './AppContainer.ts';
import { ApplicationBootstrap } from '@src/modules/shared/bootstrap/infrastructure/ApplicationBootstrap.ts';
import { createPresentationShell } from '@src/modules/shared/presentation/infrastructure/factories/createPresentationShell.ts';
import { registerShellMainWebContent } from '@src/modules/shared/presentation/infrastructure/shellMainPageRegistry.ts';
import { buildShellRouteBindings } from './shellRouteBindings.ts';
import { createOfflineSqliteInfrastructure } from '@src/core/bootstrap/offlineSqliteBootstrap.ts';
import { installLocalSqliteBeforeUnloadGuard } from '@src/modules/shared/local-sqlite/infrastructure/localSqliteBeforeUnloadGuard.ts';
import { showFatalAppError } from '@src/modules/shared/bootstrap/infrastructure/showFatalAppError.ts';

for (const [pageId, tagName] of [
  ['dashboard', 'dashboard-component'],
  ['puntos_entrega', 'puntos-entrega-page'],
  ['redencion_bono', 'redencion-bono-page'],
  ['sync', 'sync-page'],
  ['observabilidad', 'observabilidad-page'],
] as const) {
  registerShellMainWebContent(pageId, tagName);
}

/** Fail fast when an old cached bundle without inlined WASM is still running. */
const wasmProbe = getSqlJsWasmBinary();
if (wasmProbe == null || wasmProbe.byteLength === 0) {
  throw new Error(
    'Este build no incluye WASM de SQLite embebido (caché antigua). Recompile, borre datos del sitio y recargue con red.',
  );
}

document.title = AppConfig.getAppDisplayTitle();

/** Renders the fatal error UI when offline SQLite bootstrap fails. */
function showOfflineBootstrapFailure(err: unknown): void {
  showFatalAppError({
    title: 'Modo offline: no se pudo conectar la base de datos local.',
    hint: 'Compruebe la red, que el servidor sirva /database/local.db y /sqljs/sql-wasm.wasm, y pulse Reintentar.',
    error: err,
  });
}

const offlineInfra = createOfflineSqliteInfrastructure();
let offlineSqliteBootstrapOk = true;
try {
  await offlineInfra.bootstrap();
} catch (e) {
  if (AppConfig.useLocalSqliteClient()) {
    offlineSqliteBootstrapOk = false;
    showOfflineBootstrapFailure(e);
  } else {
    throw e;
  }
}

if (offlineSqliteBootstrapOk) {
  if (AppConfig.useLocalSqliteClient()) {
    installLocalSqliteBeforeUnloadGuard(() => offlineInfra.getRuntime().hasPendingPersist());
  }
  try {
    const presentation = await createPresentationShell();
    new ApplicationBootstrap(
      presentation,
      AppContainer.create({
        localSqlite: AppConfig.useLocalSqliteClient() ? offlineInfra.getRuntime() : undefined,
        puntosEntregaShell: offlineInfra.getPuntosEntregaShell(),
        redencionBonoApplication: offlineInfra.getRedencionBonoApplicationService(),
      }),
      buildShellRouteBindings,
    ).run();
  } catch (err) {
    showFatalAppError({
      title: 'No se pudo iniciar la aplicación.',
      hint: 'Falló la carga del shell Lit o el arranque del shell. Recargue o revise la consola.',
      error: err,
    });
  }
}
